/**
 * @description
 * Sync results summary component for Platform Sync dialog.
 * Displays statistics and errors from completed sync operation.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/components/PlatformSync/PlatformSyncDialog.tsx
 */

import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Users, Building2 } from 'lucide-react';
import type { SyncResult } from './types';

interface SyncResultsSummaryProps {
  result: SyncResult;
}

/**
 * Component for displaying sync results
 */
export const SyncResultsSummary: React.FC<SyncResultsSummaryProps> = ({ result }) => {
  const hasErrors =
    result.clients.errors.length > 0 || result.users.errors.length > 0;
  const totalSynced = result.clients.synced + result.users.synced;

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <div className="flex flex-col items-center justify-center py-6">
        {hasErrors ? (
          <AlertCircle className="h-16 w-16 text-orange-600" />
        ) : (
          <CheckCircle className="h-16 w-16 text-green-600" />
        )}
        <h3 className="text-2xl font-semibold text-foreground mt-4">
          {hasErrors ? 'Sync Completed with Warnings' : 'Sync Completed Successfully'}
        </h3>
        <p className="text-muted-foreground mt-2">
          {totalSynced} total records synced
        </p>
      </div>

      {/* Client Results */}
      <div className="rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="h-5 w-5 text-primary" />
          <h4 className="text-lg font-semibold text-foreground">Client Sync Results</h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="text-2xl font-bold text-green-700">
              {result.clients.synced}
            </div>
            <div className="text-sm text-green-600 mt-1">Synced</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">
              {result.clients.created}
            </div>
            <div className="text-sm text-blue-600 mt-1">Created</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-purple-50 border border-purple-200">
            <div className="text-2xl font-bold text-purple-700">
              {result.clients.updated}
            </div>
            <div className="text-sm text-purple-600 mt-1">Updated</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-2xl font-bold text-gray-700">
              {result.clients.skipped}
            </div>
            <div className="text-sm text-gray-600 mt-1">Skipped</div>
          </div>
        </div>
        {result.clients.errors.length > 0 && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-red-800">
                  {result.clients.errors.length} Error(s)
                </div>
                <ul className="mt-2 space-y-1 text-sm text-red-700">
                  {result.clients.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Results */}
      <div className="rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-5 w-5 text-primary" />
          <h4 className="text-lg font-semibold text-foreground">User Sync Results</h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="text-2xl font-bold text-green-700">
              {result.users.synced}
            </div>
            <div className="text-sm text-green-600 mt-1">Synced</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">
              {result.users.created}
            </div>
            <div className="text-sm text-blue-600 mt-1">Created</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-purple-50 border border-purple-200">
            <div className="text-2xl font-bold text-purple-700">
              {result.users.updated}
            </div>
            <div className="text-sm text-purple-600 mt-1">Updated</div>
          </div>
          <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="text-2xl font-bold text-gray-700">
              {result.users.skipped}
            </div>
            <div className="text-sm text-gray-600 mt-1">Skipped</div>
          </div>
        </div>
        {result.users.errors.length > 0 && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-red-800">
                  {result.users.errors.length} Error(s)
                </div>
                <ul className="mt-2 space-y-1 text-sm text-red-700">
                  {result.users.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */